<template>
  <div class="container-fluid pay-ops-overview py-4">
    <div class="row g-4 mb-4">
      <div class="col-12">
        <div class="card border-0 ov-hero-card overflow-hidden">
          <div class="card-body p-4 p-xl-5">
            <div class="row g-4 align-items-center">
              <div class="col-xl-8">
                <div class="d-inline-flex align-items-center gap-2 ov-eyebrow mb-3">
                  <span class="ov-eyebrow-dot"></span>
                  <span>Section Overview</span>
                </div>
                <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
                  <h2 class="mb-0"><i class="fa fa-credit-card me-2"></i>ryd Pay Operations</h2>
                  <span class="badge bg-white text-primary fw-semibold px-3 py-2">Internal</span>
                </div>
                <p class="text-muted ov-hero-copy mb-4">
                  The home of payment-operations work in the portal. Today it hosts the
                  <strong>Order Amendments</strong> workspace: triage, amend, and audit problematic
                  orders with preview safety and a complete audit trail. Each link below jumps into
                  the right page; the badges show whether your account currently has read and write
                  access for that destination.
                </p>
                <div class="d-flex flex-wrap gap-2 align-items-center">
                  <span class="text-muted small fw-semibold me-1">Quick access:</span>
                  <router-link
                    v-for="q in quickActions"
                    :key="q.path"
                    :to="q.path"
                    class="btn btn-sm btn-pill btn-outline-primary ov-quick-link"
                  >
                    <i class="fa me-1" :class="q.icon"></i>{{ q.title }}
                  </router-link>
                </div>
              </div>

              <div class="col-xl-4">
                <div class="row g-3">
                  <div class="col-6" v-for="stat in overviewStats" :key="stat.label">
                    <div class="ov-stat-card h-100">
                      <div class="ov-stat-label">{{ stat.label }}</div>
                      <div class="ov-stat-value">{{ stat.value }}</div>
                      <div class="small text-muted">{{ stat.hint }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="ribbon-wrapper card">
          <div class="card-body">
            <div class="ribbon ribbon-clip ribbon-primary">Explore by Workflow</div>
            <p class="text-muted mb-3">
              Use the overview cards below to jump into the right page. The
              <code>Read</code> badge reflects whether you can open the page; the
              <code>Write</code> badge reflects whether you can perform its primary edit action.
            </p>
            <div class="row g-2">
              <div v-for="section in workflowSections" :key="section.id" class="col-sm-6 col-xl-4">
                <a
                  class="ov-jump-link"
                  :href="`#${section.id}`"
                  @click.prevent="scrollToSection(section.id)"
                >
                  <i class="fa me-2" :class="section.icon"></i>{{ section.title }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <div v-for="section in sections" :key="section.id" class="col-12 col-xxl-6">
        <section :id="section.id" class="card ov-section-card h-100 border-0 overflow-hidden">
          <div class="card-header py-3" :class="section.headerClass">
            <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div>
                <h5 class="mb-1 text-white fw-bold">
                  <i class="fa me-2" :class="section.icon"></i>{{ section.title }}
                </h5>
                <div class="small text-white-50">{{ section.subtitle }}</div>
              </div>
              <span class="badge ov-section-count">{{ section.items.length }} pages</span>
            </div>
          </div>

          <div class="card-body p-0">
            <div class="ov-section-desc px-4 py-3">
              <p class="mb-0 text-muted small lh-sm">
                {{ section.description }}
              </p>
            </div>

            <div class="list-group list-group-flush">
              <router-link
                v-for="item in section.items"
                :key="item.path"
                :to="item.path"
                class="list-group-item list-group-item-action ov-item border-0 border-top"
              >
                <span class="ov-icon" :class="section.iconTextClass">
                  <i class="fa" :class="item.icon"></i>
                </span>
                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex align-items-start justify-content-between gap-2 flex-wrap">
                    <div class="fw-semibold text-dark ov-title">{{ item.title }}</div>
                    <span v-if="item.badge" class="badge ov-item-badge">{{ item.badge }}</span>
                  </div>
                  <small class="text-muted lh-sm d-block mb-2">{{ item.hint }}</small>
                  <div class="d-flex flex-wrap gap-2 align-items-center">
                    <span
                      class="badge ov-access-badge ov-read-badge"
                      :class="hasAccess(item.readAccess) ? 'ov-access-ok' : 'ov-access-missing'"
                      :title="accessTitle(item.readAccess)"
                    >
                      <i
                        class="fa me-1"
                        :class="hasAccess(item.readAccess) ? 'fa-eye' : 'fa-lock'"
                      />
                      Read: {{ accessLabel(item.readAccess) }}
                    </span>
                    <span
                      class="badge ov-access-badge ov-write-badge"
                      :class="hasAccess(item.writeAccess) ? 'ov-access-ok' : 'ov-access-missing'"
                      :title="accessTitle(item.writeAccess)"
                    >
                      <i
                        class="fa me-1"
                        :class="hasAccess(item.writeAccess) ? 'fa-pencil' : 'fa-lock'"
                      />
                      Write: {{ accessLabel(item.writeAccess) }}
                    </span>
                  </div>
                </div>
                <i class="fa fa-chevron-right text-muted ov-chevron"></i>
              </router-link>
            </div>
          </div>
        </section>
      </div>

      <div class="col-12">
        <aside id="access-roles" class="ov-access-panel">
          <div class="d-flex align-items-start gap-3 flex-column flex-lg-row">
            <div class="ov-access-panel-icon">
              <i class="fa fa-shield"></i>
            </div>
            <div class="flex-grow-1 min-w-0">
              <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-2">
                <div>
                  <h5 class="mb-1 fw-bold text-dark">Access &amp; Roles</h5>
                  <div class="small text-muted">
                    Permission guidance for the Pay Operations section
                  </div>
                </div>
                <span class="badge ov-access-panel-label">Section guidance</span>
              </div>

              <p class="text-muted small mb-3">
                The <code>/pay-operations</code> area is gated by the internal <code>ryd</code> user
                group and <code>RYD_INTERNAL</code> partner type. Individual links additionally
                require one of the three Pay Operations roles below.
              </p>

              <div
                class="ov-access-status mb-3"
                :class="hasRydWorkspace ? 'ov-access-status-ok' : 'ov-access-status-danger'"
              >
                <i
                  class="fa me-1"
                  :class="hasRydWorkspace ? 'fa-check-circle' : 'fa-exclamation-circle'"
                ></i>
                <span v-if="hasRydWorkspace"
                  >You are in the <code>ryd</code> user group for Pay Operations.</span
                >
                <span v-else
                  >Your account is not in the <code>ryd</code> user group required for this
                  workspace.</span
                >
              </div>

              <div class="d-flex flex-wrap gap-2 mb-3">
                <span
                  v-for="role in importantRoles"
                  :key="role"
                  class="badge ov-role-badge"
                  :class="hasRole(role) ? 'ov-role-badge-ok' : 'ov-role-badge-missing'"
                  :title="hasRole(role) ? 'You have this role' : 'You do not have this role'"
                >
                  <i class="fa me-1" :class="hasRole(role) ? 'fa-check' : 'fa-times'" />
                  {{ role }}
                </span>
              </div>

              <div class="ov-role-note">
                <div class="fw-semibold text-dark mb-1">Reading the badges</div>
                <div class="text-muted small">
                  Blue badges show read access. Amber badges show edit/write access.
                  <code>pay_operations_viewer</code> can read every page;
                  <code>pay_operations_agent</code> can execute amendments and run Manual
                  Assignment; <code>pay_operations_admin</code> additionally covers Force Complete,
                  Refund &amp; Cancel, and global audit.
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { Roles } from '@/constants/roles';

function rolesAccess(roles, title, options = {}) {
  const fallback = options.fallback ?? (roles.length ? roles.join(' or ') : '');
  return {
    roles,
    fallback,
    title,
    requireRydGroup: options.requireRydGroup ?? false,
  };
}

const payOpsAllReadRoles = [
  Roles.pay_operations_viewer,
  Roles.pay_operations_agent,
  Roles.pay_operations_admin,
];

const payOpsAgentOrAdmin = [Roles.pay_operations_agent, Roles.pay_operations_admin];

const payOpsAdminOnly = [Roles.pay_operations_admin];

const queueReadAccess = rolesAccess(
  payOpsAllReadRoles,
  'Order Amendments menu: any of the three Pay Operations roles.',
  { fallback: 'pay_operations_viewer / agent / admin' },
);

const queueWriteAccess = rolesAccess(
  payOpsAgentOrAdmin,
  'Complete / Cancel actions require pay_operations_agent or pay_operations_admin.',
  { fallback: 'pay_operations_agent or pay_operations_admin' },
);

const manualAssignmentAccess = rolesAccess(
  payOpsAgentOrAdmin,
  'Manual Assignment is restricted to pay_operations_agent and pay_operations_admin.',
  { fallback: 'pay_operations_agent or pay_operations_admin' },
);

const odooPaymentAccess = rolesAccess(
  payOpsAgentOrAdmin,
  'Odoo Payment Update is restricted to pay_operations_agent and pay_operations_admin.',
  { fallback: 'pay_operations_agent or pay_operations_admin' },
);

const auditAccess = rolesAccess(
  payOpsAdminOnly,
  'Audit log is restricted to pay_operations_admin.',
  { fallback: 'pay_operations_admin' },
);

const noPortalWriteAction = {
  roles: [],
  fallback: 'No write action',
  title: 'No primary edit or write action is exposed from this destination page.',
  requireRydGroup: false,
};

const rydGroupReadAccess = rolesAccess(
  [],
  'Page is accessible to anyone in the ryd user group with the RYD_INTERNAL partner type.',
  { fallback: 'ryd user group', requireRydGroup: true },
);

export default {
  name: 'PayOperationsOverview',
  data() {
    const userData = getUserAddDataFromLocalStorage() || {};
    const roles = Array.isArray(userData.roles) ? userData.roles : [];
    const isUserGroupRyd = !!userData.isUserGroupRyd;
    return {
      roles,
      isUserGroupRyd,
      importantRoles: [
        Roles.pay_operations_viewer,
        Roles.pay_operations_agent,
        Roles.pay_operations_admin,
      ],
      quickActions: [
        { path: '/pay-operations/order-amendments/queue', title: 'Queue', icon: 'fa-list' },
        {
          path: '/pay-operations/order-amendments/manual-assignment',
          title: 'Manual Assignment',
          icon: 'fa-hand-pointer-o',
        },
        {
          path: '/pay-operations/order-amendments/incidents',
          title: 'Incidents',
          icon: 'fa-flag-o',
        },
        { path: '/pay-operations/order-amendments/guide', title: 'Guide', icon: 'fa-book' },
      ],
      sections: [
        {
          id: 'workflow-pages',
          title: 'Order Amendment Workflow',
          subtitle: 'Daily case-handling pages',
          description:
            'Triage problematic orders from the queue, group related cases into incidents, run manual assignment when automatic classification missed something, and review the audit log for completed actions.',
          icon: 'fa-list',
          headerClass: 'bg-primary',
          iconTextClass: 'text-primary',
          items: [
            {
              path: '/pay-operations/order-amendments/queue',
              title: 'Queue',
              hint: 'Filter the active workflow, then open a case to review and amend the order. Replaces the legacy Google Sheet for problematic orders.',
              icon: 'fa-list',
              readAccess: queueReadAccess,
              writeAccess: queueWriteAccess,
            },
            {
              path: '/pay-operations/order-amendments/manual-assignment',
              title: 'Manual Assignment',
              hint: 'Add orders to an incident or override an automatic classification by submitting one or more UUIDs.',
              icon: 'fa-hand-pointer-o',
              readAccess: manualAssignmentAccess,
              writeAccess: manualAssignmentAccess,
            },
            {
              path: '/pay-operations/order-amendments/incidents',
              title: 'Incidents',
              hint: 'Named groupings of related orders. Replaces the free-text comment column in the legacy spreadsheet.',
              icon: 'fa-flag-o',
              readAccess: queueReadAccess,
              writeAccess: rolesAccess(
                payOpsAgentOrAdmin,
                'Creating, renaming, or closing incidents requires pay_operations_agent or pay_operations_admin.',
                { fallback: 'pay_operations_agent or pay_operations_admin' },
              ),
            },
            {
              path: '/pay-operations/order-amendments/odoo-payment-update',
              title: 'Odoo Payment Update',
              hint: 'Attach a payment object to an Odoo invoice when order-service did not produce one — the fix for drive-away invoices failing with a missing payment.',
              icon: 'fa-credit-card',
              readAccess: odooPaymentAccess,
              writeAccess: odooPaymentAccess,
            },
            {
              path: '/pay-operations/order-amendments/audit',
              title: 'Audit',
              hint: 'Read-only log of every executed amendment. Restricted to admin roles for compliance traceability.',
              icon: 'fa-history',
              readAccess: auditAccess,
              writeAccess: noPortalWriteAction,
            },
          ],
        },
        {
          id: 'reference',
          title: 'Reference',
          subtitle: 'How-it-works documentation',
          description:
            'The Guide is the long-form explanation of the working pages, the vocabulary, the process flows, the states, and the FAQ. Use it for onboarding or when something looks off in the queue.',
          icon: 'fa-book',
          headerClass: 'bg-secondary',
          iconTextClass: 'text-dark',
          items: [
            {
              path: '/pay-operations/order-amendments/guide',
              title: 'Guide',
              hint: 'In-depth explanation of every page, flow, role, and state. Start here if you are new to Order Amendments.',
              icon: 'fa-book',
              badge: 'Docs',
              readAccess: rydGroupReadAccess,
              writeAccess: noPortalWriteAction,
            },
          ],
        },
      ],
    };
  },
  computed: {
    hasRydWorkspace() {
      return this.isUserGroupRyd;
    },
    workflowSections() {
      return [
        ...this.sections.map((section) => ({
          id: section.id,
          title: section.title,
          icon: section.icon,
        })),
        {
          id: 'access-roles',
          title: 'Access & Roles',
          icon: 'fa-lock',
        },
      ];
    },
    overviewStats() {
      const totalSections = this.sections.length;
      const totalPages = this.sections.reduce((sum, section) => sum + section.items.length, 0);
      const accessIsGated = (access) => access.roles.length > 0 || !!access.requireRydGroup;
      const roleProtectedPages = this.sections.reduce(
        (sum, section) =>
          sum +
          section.items.filter(
            (item) => accessIsGated(item.readAccess) || accessIsGated(item.writeAccess),
          ).length,
        0,
      );
      return [
        { label: 'Sections', value: totalSections, hint: 'Main work areas' },
        { label: 'Pages', value: totalPages, hint: 'Direct entry points' },
        { label: 'Role-gated', value: roleProtectedPages, hint: 'Page or write roles' },
        { label: 'Access', value: this.hasRydWorkspace ? 'OK' : 'Check', hint: 'ryd user group' },
      ];
    },
  },
  methods: {
    hasRole(role) {
      return this.roles.includes(role);
    },
    hasAccess(access) {
      const rydOk = !access.requireRydGroup || this.isUserGroupRyd;
      const rolesOk = !access.roles.length || access.roles.some((role) => this.hasRole(role));
      return rydOk && rolesOk;
    },
    accessLabel(access) {
      return access.fallback;
    },
    accessTitle(access) {
      return access.title;
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      const offset = 84;
      if (!element) return;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
.ov-hero-card {
  background:
    radial-gradient(circle at top right, rgba(0, 161, 254, 0.14), transparent 32%),
    linear-gradient(135deg, rgba(248, 250, 255, 0.98), rgba(255, 255, 255, 0.98));
  border: 1px solid rgba(0, 161, 254, 0.1);
  box-shadow: 0 18px 38px rgba(24, 39, 75, 0.06);
}

.ov-eyebrow {
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--theme-default);
}

.ov-eyebrow-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--theme-default);
}

.ov-hero-copy {
  max-width: 58rem;
  font-size: 0.98rem;
}

.ov-quick-link {
  background: rgba(255, 255, 255, 0.86);
}

.ov-stat-card {
  padding: 1rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(0, 161, 254, 0.08);
}

.ov-stat-label {
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: #6c757d;
}

.ov-stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.1;
  color: #212529;
  margin: 0.2rem 0 0.35rem;
}

.ov-jump-link {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.7rem 0.85rem;
  border-radius: 0.8rem;
  text-decoration: none;
  color: #303e49;
  background: rgba(248, 250, 255, 0.88);
  border: 1px solid rgba(0, 161, 254, 0.08);
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}

.ov-jump-link:hover {
  background: rgba(0, 161, 254, 0.06);
  border-color: rgba(0, 161, 254, 0.18);
  transform: translateY(-1px);
}

.ov-section-card {
  border: 1px solid rgba(33, 37, 41, 0.06);
  box-shadow: 0 14px 30px rgba(24, 39, 75, 0.05);
}

.ov-section-count {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.18);
  font-weight: 600;
}

.ov-section-desc {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 255, 0.62);
}

.ov-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0.95rem 1rem;
  transition:
    background-color 0.18s ease,
    transform 0.18s ease;
}

.ov-item:hover {
  background: rgba(248, 250, 255, 0.94);
}

.ov-icon {
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: rgba(33, 37, 41, 0.05);
  font-size: 0.92rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.ov-title {
  font-size: 0.92rem;
  margin-bottom: 0.2rem;
}

.ov-item-badge,
.ov-access-badge {
  font-weight: 600;
}

.ov-item-badge {
  background: rgba(33, 37, 41, 0.06);
  color: #6c757d;
  border: 1px solid rgba(33, 37, 41, 0.08);
}

.ov-access-badge {
  white-space: normal;
  text-align: left;
  border: 1px solid transparent;
}

.ov-read-badge.ov-access-ok {
  color: #075985;
  background: #e0f2fe;
  border-color: #bae6fd;
}

.ov-read-badge.ov-access-missing {
  color: #64748b;
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.ov-write-badge.ov-access-ok {
  color: #92400e;
  background: #fef3c7;
  border-color: #fde68a;
}

.ov-write-badge.ov-access-missing {
  color: #78716c;
  background: #f5f5f4;
  border-color: #d6d3d1;
}

.ov-access-panel {
  padding: 1.25rem;
  border-radius: 0.9rem;
  background: linear-gradient(135deg, rgba(224, 242, 254, 0.82), rgba(255, 247, 237, 0.76));
  border: 1px solid rgba(14, 116, 144, 0.16);
  box-shadow: inset 4px 0 0 rgba(0, 161, 254, 0.72);
}

.ov-access-panel-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #075985;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(14, 116, 144, 0.14);
}

.ov-access-panel-label {
  color: #075985;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(14, 116, 144, 0.14);
  font-weight: 700;
}

.ov-access-status {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.7rem;
  border-radius: 0.7rem;
  font-weight: 700;
  font-size: 0.85rem;
}

.ov-access-status-ok {
  color: #166534;
  background: rgba(220, 252, 231, 0.86);
  border: 1px solid rgba(134, 239, 172, 0.78);
}

.ov-access-status-danger {
  color: #991b1b;
  background: rgba(254, 226, 226, 0.86);
  border: 1px solid rgba(252, 165, 165, 0.78);
}

.ov-role-badge {
  font-weight: 700;
  border: 1px solid transparent;
}

.ov-role-badge-ok {
  color: #166534;
  background: #dcfce7;
  border-color: #86efac;
}

.ov-role-badge-missing {
  color: #64748b;
  background: rgba(255, 255, 255, 0.78);
  border-color: #cbd5e1;
}

.ov-chevron {
  font-size: 0.75rem;
  flex-shrink: 0;
  margin-top: 0.35rem;
}

.ov-role-note {
  padding: 0.95rem 1rem;
  border-radius: 0.9rem;
  background: rgba(248, 250, 255, 0.82);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.min-w-0 {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .ov-hero-copy {
    font-size: 0.94rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ov-jump-link,
  .ov-item {
    transition: none;
  }

  .ov-jump-link:hover {
    transform: none;
  }
}
</style>
